/**
 * People tab data (GET /api/dashboard/people, /people/:id).
 * Design: screenshots/18-people-directory.png, 19-people-profile-official.png, 20-people-profile-resident.png
 */
import type { Seconds } from "../types";

export type PersonType = "official" | "resident" | "lobbyist" | "org_rep";

/** Officials use their official portrait. Everyone else gets a video frame, labelled as such. */
export type PersonImage =
  | { kind: "portrait"; url: string }
  | { kind: "frame"; url: string; meetingId: string; at: Seconds }; // UI label: "Frame at 38:12", never "photo"

export interface Evidence {
  meetingId: string;
  meetingLabel: string;   // "City Council · Sep 29"
  at: Seconds;            // "Watch at 1:20:36"
  quote: string;          // verbatim from the transcript
}

export interface PolicyPosition {
  id: string;
  label: string;                         // "Parking meter concession transfer"
  recordId?: string;                     // "SO2026-0025407" → links to Legislation
  areaId: string;                        // "budget-finance"
  stance: "supports" | "opposes" | "mixed";
  vote?: "yes" | "no" | "abstain";       // when a roll call exists
  evidence: Evidence[];                  // at least one; shown as the quote + watch link
  source: "statement" | "testimony";     // residents: usually "testimony"
}

/**
 * Political leaning is an AI ESTIMATE. Shown only with: the "AI estimate" chip, a confidence
 * level, the range band, the evidence count behind it, and that evidence.
 * score: -1 = most progressive … 0 = moderate … +1 = most conservative.
 *
 * Officials: built from divided roll calls AND quotes ("Based on 91 divided votes and 6 quotes").
 * Votes are the stronger signal, so an official is ready at MIN_VOTES divided votes even with few quotes.
 * Everyone else: quotes only (MIN_QUOTES).
 */
export type LeaningEstimate =
  | { status: "estimated"; score: number; low: number; high: number; confidence: "low" | "medium" | "high";
      votesUsed: number; quotesUsed: number; meetingsUsed: number; updatedAt: string;
      votes: VoteEvidence[]; evidence: Evidence[] }
  | { status: "building"; votesUsed: number; quotesUsed: number; quotesRequired: number };  // "Building · 3 of 5 quotes"

/** A recorded roll call used as leaning evidence (profile: "Votes behind this estimate"). */
export interface VoteEvidence { recordId: string; title: string; vote: "yea" | "nay"; tally: string; date: string }

export interface PersonSummary {
  id: string;
  name: string;
  type: PersonType;
  ward?: number;
  organization?: { id: string; name: string };
  image: PersonImage;
  appearances: number;
  areas: string[];             // top policy areas (labels)
  leaning: LeaningEstimate;
  lastSpoke?: { meetingId: string; date: string; at: Seconds };
}

export interface PersonProfile extends PersonSummary {
  moments: number;
  positions: PolicyPosition[];
  areaShares: { area: string; share: number }[];   // 0..1, "share of speaking time this term"
  appearancesList: { meetingId: string; meetingLabel: string; date: string; from: Seconds; to: Seconds; summary: string; frame: PersonImage }[];
}

/**
 * Product decision: leaning is estimated for EVERY person type, residents included.
 * Safeguards that make that acceptable (enforced in UI and API):
 *  - always labelled "AI estimate" with confidence, range, and the quotes behind it
 *  - below MIN_QUOTES (or MIN_VOTES for officials) the profile shows "Building · n of 5 quotes" instead of a guess
 *  - anyone can "Flag as inaccurate" (admin review queue)
 *  - residents can claim their profile ("This is me") to see how it was made, add context, or request removal
 */
export const LEANING_ALLOWED: PersonType[] = ["official", "resident", "lobbyist", "org_rep"];
export const leaningAllowed = (t: PersonType) => LEANING_ALLOWED.includes(t);
export const MIN_QUOTES = 5;
export const MIN_VOTES = 20;

/** Enough evidence to show a score instead of "Building"? */
export const leaningReady = (t: PersonType, votesUsed: number, quotesUsed: number) =>
  t === "official" ? votesUsed >= MIN_VOTES || quotesUsed >= MIN_QUOTES : quotesUsed >= MIN_QUOTES;

/** "Based on 91 divided votes and 6 quotes from 4 meetings" */
export const leaningBasis = (e: { votesUsed: number; quotesUsed: number; meetingsUsed: number }) =>
  "Based on " + [e.votesUsed ? `${e.votesUsed} divided votes` : "", e.quotesUsed ? `${e.quotesUsed} quotes from ${e.meetingsUsed} meetings` : ""]
    .filter(Boolean).join(" and ");

/** Map a -1..+1 score to a 0..100 % position on the meter (progressive on the left). */
export const leaningToPct = (score: number) => Math.round(((Math.max(-1, Math.min(1, score)) + 1) / 2) * 100);

export const leaningLabel = (score: number) => (score < -0.33 ? "Progressive" : score > 0.33 ? "Conservative" : "Moderate");
